import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  Subject,
  takeUntil,
} from 'rxjs';
import { JobCounts } from 'server/shared/helpers/getJobCounts';
import { Job } from 'server/shared/models/job';
import { InviteeStatusEnum } from 'server/shared/models/jobInviteeStatusEnum';
import { JobStatusEnum } from 'server/shared/models/jobStatusEnum';
import { filterIsNotEmpty } from '../../../helpers/filterIsNotEmpty';
import { Column } from '../../../helpers/job/jobColumns';
import { CustomerService } from '../../services/customer.service';
import { UserService } from '../../services/user.service';
import { TransferCounts } from '../job-list-item-transfers/job-list-item-transfers.component';

@Component({
  selector: 'app-dispo-job-list-item-compact-content',
  templateUrl: './dispo-job-list-item-compact-content.component.html',
  styleUrls: [
    './dispo-job-list-item-compact-content.component.scss',
    '../../../stylesheets/components/atoms/jobs-list-view.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DispoJobListItemCompactContentComponent {
  private destroyed$ = new Subject<void>();

  job$ = new BehaviorSubject<Job | undefined>(undefined);
  @Input() set job(job: Job | undefined) {
    this.job$.next(job);
  }
  get job(): Job | undefined {
    return this.job$.getValue();
  }

  counts$ = new BehaviorSubject<JobCounts | undefined>(undefined);
  @Input() set counts(value: JobCounts | undefined) {
    this.counts$.next(value);
  }
  get counts(): JobCounts | undefined {
    return this.counts$.value;
  }

  transferCounts$ = new BehaviorSubject<TransferCounts | undefined>(undefined);
  @Input() set transferCounts(value: TransferCounts | undefined) {
    this.transferCounts$.next(value);
  }
  get transferCounts(): TransferCounts | undefined {
    return this.transferCounts$.value;
  }

  transferredToMeText$ = new BehaviorSubject<string | undefined>(undefined);
  @Input() set transferredToMeText(value: string | undefined) {
    this.transferredToMeText$.next(value);
  }
  get transferredToMeText(): string | undefined {
    return this.transferredToMeText$.value;
  }

  columns$ = new BehaviorSubject<Column[]>([]);
  @Input() set columns(columns: Column[]) {
    this.columns$.next(columns);
  }
  get columns(): Column[] {
    return this.columns$.value;
  }

  loggedInCustomer$ = this.customerService.readyDispoCustomer$;

  loggedInUser$ = this.userService.readyDispo$;

  isOwnedOrganisationJob$ = combineLatest([
    this.loggedInCustomer$,
    this.job$.pipe(filter(filterIsNotEmpty)),
  ]).pipe(
    map(([ownCustomer, job]) => {
      return (
        ownCustomer?._id !== job.customer.id &&
        job.ownerCustomerIds?.some((owner) => owner === ownCustomer?._id)
      );
    }),
  );

  jobCertificates$ = this.job$.pipe(
    filter(filterIsNotEmpty),
    map((job) => {
      if (!job.certificates?.length) {
        return;
      }
      return job.certificates.map((c) => c.displayName).join(', ');
    }),
  );

  extraInviteeCount$ = new BehaviorSubject<number>(0);

  compactOverviewInvitee$ = combineLatest([
    this.job$,
    this.loggedInCustomer$,
  ]).pipe(
    filter(filterIsNotEmpty),
    takeUntil(this.destroyed$),
    map(([job, customer]) => {
      if (!job || !customer) return;
      let bookedInvitees = job.invitees
        .filter(
          (invitee) =>
            invitee.status === InviteeStatusEnum.BOOKED ||
            invitee.status === InviteeStatusEnum.CANCELED,
        )
        .sort((a, b) =>
          a.status === InviteeStatusEnum.BOOKED
            ? -1
            : b.status === InviteeStatusEnum.BOOKED
            ? 1
            : 0,
        );
      this.extraInviteeCount$.next(bookedInvitees.length - 1);
      return bookedInvitees[0];
    }),
  );

  constructor(
    private customerService: CustomerService,
    private userService: UserService,
  ) {}

  readonly JobStatusEnum = JobStatusEnum;

  trackByColumnKey = (index: number, column: Column) => column.key;
}
